import { ReactProductCard } from '@/components/react/ui/cards/ReactProductCard';
import { ReactAccordion } from '@/components/react/ui/ReactAccordion';
import { ReactAccordionItem } from '@/components/react/ui/ReactAccordionItem';
import { ReactSelect } from '@/components/react/ui/ReactSelect';
import type { HTMLAttributes } from 'astro/types';
import { cx } from 'cva';
import keyBy from 'lodash/keyBy';
import type { ComponentProps } from 'react';
import React from 'react';
import invariant from 'tiny-invariant';

export type CompareKey = 'compare1' | 'compare2' | 'compare3';

interface ComparisonProps<T extends Record<keyof T, string>>
  extends HTMLAttributes<'div'> {
  groups: ComparisonGroup<T>[];
  items: ComparisonItem<T>[];
  initialSelections: Record<CompareKey, ComparisonItem<T>['value']>;
}

export interface ComparisonItem<T extends Record<keyof T, string>> {
  label: string;
  value: string;
  item: T;
  card: ComponentProps<typeof ReactProductCard>;
}

type ComparisonField<T extends Record<keyof T, string>> = keyof T;

export interface ComparisonGroup<T extends Record<keyof T, string>> {
  label: string;
  fields: ComparisonField<T>[];
}

function ComparisonSelectColumn<T extends Record<keyof T, string>>({
  className,
  compareKey,
  compareSelections,
  items,
  itemMap,
  onSelect,
}: {
  className?: string;
  compareKey: CompareKey;
  compareSelections: Record<CompareKey, ComparisonItem<T>['value']>;
  items: ComparisonItem<T>[];
  itemMap: Record<ComparisonItem<T>['value'], ComparisonItem<T>>;
  onSelect: (
    key: CompareKey,
    value: ComparisonItem<T>['value'] | undefined,
  ) => void;
}) {
  const itemValue = compareSelections[compareKey];
  const item = itemMap[itemValue];

  const onChange = React.useCallback(
    (value: string | undefined) => {
      onSelect(compareKey, value);
    },
    [compareKey, onSelect],
  );

  if (!item) {
    throw new Error('Comparison item selection invalid');
  }

  return (
    <div data-compare-item className={cx('flex flex-col gap-6', className)}>
      <ReactSelect
        label="Select"
        items={items}
        errorMessage=""
        defaultValue={itemValue}
        onValueChange={onChange}
      />
      <ReactProductCard {...item.card} rootClassName="max-sm:min-w-0" />
    </div>
  );
}

function ComparisonDataColumn<T extends Record<keyof T, string>>({
  className,
  item,
  group,
}: {
  className?: string;
  item: T | undefined;
  group: ComparisonGroup<T>;
}) {
  if (!item) {
    throw new Error('Comparison item selection invalid');
  }

  return (
    <div className={cx('flex flex-col gap-6', className)}>
      {group.fields.map((field, idx) => (
        <p key={idx} className="text-blurb-text-bold text-lg leading-relaxed">
          {item[field]}
        </p>
      ))}
    </div>
  );
}

export function ComparisonModule<T extends Record<keyof T, string>>({
  children,
  class: className,
  items,
  groups,
  initialSelections,
}: React.PropsWithChildren<ComparisonProps<T>>) {
  invariant(groups.length >= 4, 'Number of groups must be at least 4');
  invariant(groups.length <= 10, 'Number of groups cannot be more than 10');

  const [compareSelections, selectItem] = React.useReducer(
    (
      state,
      payload: {
        compareKey: CompareKey;
        itemValue: ComparisonItem<T>['value'];
      },
    ) => ({
      ...state,
      [payload.compareKey]: payload.itemValue,
    }),
    initialSelections,
  );

  const itemMap = React.useMemo(() => keyBy(items, 'value'), [items]);

  const onSelect = React.useCallback(
    (compareKey: CompareKey, value: ComparisonItem<T>['value'] | undefined) => {
      invariant(value && itemMap[value], 'Comparison item selection invalid');

      selectItem({ compareKey, itemValue: value });
    },
    [itemMap, selectItem],
  );

  return (
    <div className={cx('flex w-full flex-col gap-10 lg:gap-12', className)}>
      {children}
      <div className="grid w-full grid-cols-2 gap-6 lg:grid-cols-3">
        <ComparisonSelectColumn
          compareKey="compare1"
          items={items}
          itemMap={itemMap}
          compareSelections={compareSelections}
          onSelect={onSelect}
        />
        <ComparisonSelectColumn
          compareKey="compare2"
          items={items}
          itemMap={itemMap}
          compareSelections={compareSelections}
          onSelect={onSelect}
        />
        <ComparisonSelectColumn
          className="hidden lg:flex"
          compareKey="compare3"
          items={items}
          itemMap={itemMap}
          compareSelections={compareSelections}
          onSelect={onSelect}
        />
      </div>
      <ReactAccordion variant="comparison">
        {groups.map((group, idx) => (
          <ReactAccordionItem
            key={idx}
            variant="comparison"
            heading={group.label}
          >
            <div className="grid grid-cols-2 gap-x-6 lg:grid-cols-3">
              <ComparisonDataColumn
                group={group}
                item={itemMap[compareSelections.compare1]?.item}
              />
              <ComparisonDataColumn
                group={group}
                item={itemMap[compareSelections.compare2]?.item}
              />
              <ComparisonDataColumn
                className="hidden lg:flex"
                group={group}
                item={itemMap[compareSelections.compare3]?.item}
              />
            </div>
          </ReactAccordionItem>
        ))}
      </ReactAccordion>
    </div>
  );
}
